import { capitalize, debounce, uniqueId } from "lodash";
import { default as TableIcon } from "../../assets/icons/table.svg?react";
import { default as LinkIcon } from "../../assets/icons/link.svg?react";
import styles from "./styles.module.scss";
import { cn } from "@/lib/utils";
import { Badge, Popover, PopoverContent, PopoverTrigger, Stack } from "@altimateai/lego";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconX } from "@tabler/icons-react";
import { default as UserIcon } from "@/assets/icons/user.svg?react";
import { default as RobotIcon } from "@/assets/icons/robot.svg?react";
import { Tooltip as UICoreTooltip } from "@altimateai/lego";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import SaveFilters from "./SaveFilters";
import ClearFilters from "./ClearFilters";
import type { Filter, FilterApplicableFor } from "./types";
import { flattenFilters } from "./utils";
import ColorTagsWithInfo from "./ColorTagsWithInfo";
import ColorInsightTags from "./ColorInsightTags";
import ColorTag from "./ColorTag";
import { BaseUser, UserListResponse } from "@/types";
import { Column } from "@/pages/datasets/types";
import z from "zod";
import { ContractTableSchema } from "@/pages/contracts/types";

type RectColor = "lightGreen" | "purple" | "pink";
export type Level = "warning" | "error";
type BgColor = "grey" | "white" | string;

const LEVEL_COLOR = {
  warning: styles.level_orange,
  error: styles.level_red,
};

const RECT_COLOR_MAP = {
  lightGreen: styles.level_light_green,
  purple: styles.level_purple,
  pink: styles.level_pink,
};

interface RectColorTagProps {
  color: RectColor | string;
  label: string;
}

const RectColorTag = ({ color, label }: RectColorTagProps) => (
  <div className={cn(styles.level_tag_rectangle, RECT_COLOR_MAP[color as RectColor] || color)}>
    {label}
  </div>
);

interface LevelTagProps {
  level?: Level;
}

const LevelTag = ({ level }: LevelTagProps) => (
  <div className={cn(styles.level_tag, level ? LEVEL_COLOR[level] : undefined)}>
    {level ? capitalize(level) : null}
  </div>
);

interface ModelTagProps {
  label: string;
}

const ModelTag = ({ label }: ModelTagProps) => {
  return (
    <div className={cn(styles.model_tag, "overflow-x-hidden")}>
      <TableIcon className="me-2" />
      <div className="text-overflow">{label}</div>
    </div>
  );
};

interface UserTagProps {
  label: string;
}

const UserTag = ({ label }: UserTagProps) => {
  return (
    <div className={cn(styles.model_tag, "overflow-x-hidden")}>
      <UserIcon className="me-2" />
      <div className="text-overflow">{label}</div>
    </div>
  );
};

interface UserInfoProps {
  user: BaseUser | undefined;
}

const UserInfo = ({ user }: UserInfoProps) => {
  const firstName = user?.first_name;
  const lastName = user?.last_name;
  const email = user?.email;

  const label =
    firstName === email ? (
      email
    ) : (
      <Stack direction="column" className="gap-xs">
        <div>
          {firstName} {lastName}
        </div>
        <div>{email}</div>
      </Stack>
    );
  return (
    <UICoreTooltip title={label}>
      <div className={cn(styles.userInfo)}>
        <UserIcon className={styles.userInfoImage} />
        <div className={styles.userName}>{firstName}</div>
      </div>
    </UICoreTooltip>
  );
};

interface TableTagProps {
  table: z.infer<typeof ContractTableSchema>;
}

const TableTag = ({ table }: TableTagProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const debounceFn = useCallback(debounce(setIsOpen, 500, { trailing: true }), [setIsOpen]);
  const navigate = useNavigate();
  const id = useMemo(() => uniqueId("table-tag-") + table.resource_id, [table.resource_id]);
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div
          className={cn(styles.tag, "overflow-x-hidden")}
          onMouseEnter={() => debounceFn(true)}
          onMouseLeave={() => debounceFn(false)}
        >
          <TableIcon className="me-2" />
          <div id={id} className="fw-semibold text-overflow">
            {table.tablename}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent side="bottom">
        <div onMouseEnter={() => debounceFn(true)} onMouseLeave={() => debounceFn(false)}>
          <div
            className={cn("p-2 text-primary", styles.link)}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(false);
              navigate("/datasets/incidents", {
                state: {
                  table: {
                    id: table.resource_id,
                    scope: ["", table.database, table.schema, table.tablename],
                  },
                },
              });
            }}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(false);
                navigate("/datasets/incidents", {
                  state: {
                    table: {
                      id: table.resource_id,
                      scope: ["", table.database, table.schema, table.tablename],
                    },
                  },
                });
              }
            }}
          >
            <LinkIcon />
            <p>{table.path}</p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

interface TableTagsProps {
  tables: z.infer<typeof ContractTableSchema>[];
}

const TableTags = ({ tables }: TableTagsProps) => (
  <div className={styles.tags}>
    {tables.map((item: z.infer<typeof ContractTableSchema>) => (
      <TableTag key={item.resource_id} table={item} />
    ))}
  </div>
);

interface TableTagsWithMoreProps {
  tables: z.infer<typeof ContractTableSchema>[];
  moreClick: () => void;
}

const TableTagsWithMore = ({ tables, moreClick }: TableTagsWithMoreProps) => (
  <div className={styles.tags}>
    {tables.map((item: z.infer<typeof ContractTableSchema>) => (
      <TableTag key={item.resource_id} table={item} />
    ))}
    <span
      className={styles.more}
      onClick={moreClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") moreClick();
      }}
    >
      more
    </span>
  </div>
);

const BetaTag = ({ className }: { className?: string }) => (
  <Badge className={cn("!al-bg-orange-50 !al-text-orange-500 !al-py-0", className)}>Beta</Badge>
);

interface CustomTagProps {
  name: string;
}

export const CustomTag = ({ name }: CustomTagProps) => (
  <div className={styles.beta_tag}>{name}</div>
);

interface TagsProps {
  children: React.ReactNode;
}

const Tags = ({ children }: TagsProps) => <div className={styles.tags}>{children}</div>;

const BG_COLOR: Record<string, string> = {
  grey: "#f5f6f7",
  white: "#ffffff",
};

const DEFAULT_DISPLAY_COUNT = 5;

interface FilterTagWrapperProps {
  filters: Record<string, Filter>;
  applicableFor?: FilterApplicableFor;
}

const FilterTagWrapper = ({ filters, applicableFor }: FilterTagWrapperProps) => {
  const [displayCount, setDisplayCount] = useState(DEFAULT_DISPLAY_COUNT);
  const [showMore, setShowMore] = useState(true);

  // Flatten the filters into a single array
  const flatFilters = flattenFilters(filters);

  const handleMore = () => {
    setDisplayCount(flatFilters.length); // Display all filters
    setShowMore(false);
  };

  const handleLess = () => {
    setDisplayCount(DEFAULT_DISPLAY_COUNT); // Display only 5 filters
    setShowMore(true);
  };

  if (flatFilters.length === 0) {
    return null;
  }

  return (
    <div className={styles.filter_tags} aria-label="Filters">
      {flatFilters.slice(0, displayCount).map((filter, index) => (
        <FilterTag
          key={
            Array.isArray(filter.filterStr)
              ? filter.filterStr.join("-")
              : filter.filterStr || String(index)
          }
          searchKey={filter.searchKey}
          label={filter.label}
          filterStr={
            Array.isArray(filter.filterStr) ? filter.filterStr.join(", ") : filter.filterStr
          }
          onClose={filter.onclose}
          bgColor={(filter.bgColor as BgColor) || "grey"}
        />
      ))}
      {flatFilters.length > DEFAULT_DISPLAY_COUNT &&
        (showMore ? (
          <Stack
            className="text-primary cursor-pointer"
            align="center"
            onClick={handleMore}
            role="button"
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") handleMore();
            }}
          >
            More
          </Stack>
        ) : (
          <Stack
            className="text-primary cursor-pointer"
            align="center"
            onClick={handleLess}
            role="button"
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") handleLess();
            }}
          >
            Less
          </Stack>
        ))}
      {applicableFor && flatFilters.length > 0 && (
        <>
          <ClearFilters />
          <SaveFilters applicableFor={applicableFor} filters={filters} />
        </>
      )}
    </div>
  );
};

interface FilterTagProps {
  label: string;
  searchKey?: string;
  filterStr: string;
  onClose?: (filterStr: string) => void;
  bgColor?: BgColor;
  disableClose?: boolean;
}

const FilterTag = ({
  label,
  searchKey,
  filterStr,
  onClose,
  bgColor = "grey",
  disableClose = false,
}: FilterTagProps) => {
  const { deleteSearchParamsByKeyValue } = useFiltersBySearchParams();

  const handleRemove = () => {
    if (searchKey) {
      deleteSearchParamsByKeyValue({ [searchKey]: filterStr });
    }
    onClose?.(filterStr);
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    handleRemove();
  };
  return (
    label && (
      <div
        className={styles.filter_tag}
        style={{ background: BG_COLOR[bgColor] }}
        aria-label={`Filtered by ${label}: ${filterStr}`}
      >
        <span>
          {label}: <span className="text-primary">{filterStr}</span>
        </span>
        {!disableClose ? (
          <div
            className={styles.close_icon}
            onClick={handleClick}
            role="button"
            aria-label={`Remove ${label}: ${filterStr}`}
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                handleRemove();
              }
            }}
          >
            <IconX size={12} />
          </div>
        ) : null}
      </div>
    )
  );
};

interface ModifiedByTagProps {
  users?: UserListResponse["users"];
  lastModifiedBy: Column["last_modified_by"];
}

const ModifiedByTag = ({ users, lastModifiedBy }: ModifiedByTagProps) => {
  let label = "";
  if (lastModifiedBy === -1 || !lastModifiedBy) label = "Datapilot";
  else label = users?.find(u => u.id === lastModifiedBy)?.first_name || "Datapilot";
  return (
    <div className={styles.last_update}>
      {lastModifiedBy === -1 ? <RobotIcon className={"al-text-brand"} /> : <UserIcon />}
      <div>{label}</div>
    </div>
  );
};

export {
  TableTags,
  TableTagsWithMore,
  LevelTag,
  ColorTag,
  BetaTag,
  FilterTag,
  FilterTagWrapper,
  Tags,
  RectColorTag,
  ModifiedByTag,
  ColorInsightTags,
  ColorTagsWithInfo,
  ModelTag,
  UserTag,
  UserInfo,
};
